// After a deploy the new service worker takes over at once (src/sw.ts), but the page that's open
// still runs the old code and the old pack. It reloads the next time it's out of sight (the app is
// in the background, or the screen is off), never while someone is tapping it. A page left open
// for days also asks for a new worker each time it comes back, at most once an hour.

const CHECK_EVERY_MS = 3600_000
const RELOADED_KEY = 'tq-chunk-reload'

type Win = Pick<Window, 'navigator' | 'document' | 'location' | 'addEventListener' | 'sessionStorage'>

export function watchForUpdates(win: Win = window, now: () => number = Date.now): void {
  const sw = win.navigator.serviceWorker as ServiceWorkerContainer | undefined
  if (!sw) return
  // The first install also claims the page; that isn't an update.
  let hadController = !!sw.controller
  let pending = false
  let checkedAt = now()
  const reloadIfHidden = () => {
    if (pending && win.document.visibilityState === 'hidden') win.location.reload()
  }
  sw.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true
      return
    }
    pending = true
    reloadIfHidden()
  })
  win.document.addEventListener('visibilitychange', () => {
    reloadIfHidden()
    if (win.document.visibilityState !== 'visible' || now() - checkedAt < CHECK_EVERY_MS) return
    checkedAt = now()
    void sw.getRegistration().then((reg) => reg?.update()).catch(() => undefined)
  })
  // An old page asking for a code chunk the deploy removed (a lazy island, a sheet): load the new
  // code now. If that didn't help (a reload in the last minute), the error stands: no reload loop.
  win.addEventListener('vite:preloadError', (e) => {
    try {
      if (now() - Number(win.sessionStorage.getItem(RELOADED_KEY) ?? 0) < 60_000) return
      win.sessionStorage.setItem(RELOADED_KEY, String(now()))
    } catch {
      return
    }
    e.preventDefault()
    win.location.reload()
  })
}
